// Testing events (onsite visits): schedule, load the roster from a CSV, start each person's assessment. An event has a host
// organization and can have others (2026-10-20); people from elsewhere can make up at it.
import { DocumentsCard } from '../components/Documents.tsx';
import { Fragment, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { api, canSeeRestricted, get, isStaff, patch, post } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useData } from '../lib/useData.ts';
import { TakeOffline } from './Field.tsx';
import { localToday, type Department } from './Participants.tsx';
import { ArchivedMatches } from '../components/ArchivedMatches.tsx';
import type { VisitType } from '../components/Intake.tsx';
import { NewVisitChooser } from '../components/VisitChooser.tsx';
import { CoverSheetTopBar, ScanPilesCard } from './CoverSheets.tsx';
import { Modal } from '../components/Modal.tsx';
import { EVENT_TYPES, STAGE_LABEL, STAGES, type EventStage } from '../lib/eventStatus.ts';

interface EventRow { id: string; departmentId: string; departmentName: string; startsOn: string; endsOn: string | null; location: string | null; status: string; assessments: number;
  otherOrganizations?: string[]; stage: EventStage; eventType: string | null; team: string | null; statusNote: string | null; signed: number }

type SortKey = 'startsOn' | 'org' | 'eventType' | 'team' | 'location' | 'stage' | 'assessments';
const STAGE_ORDER = Object.fromEntries(STAGES.map((s, i) => [s.key, i])) as Record<string, number>;
const LIST_PREFS = 'ngf.events.list.v1';
interface ListPrefs { sort: SortKey; asc: boolean; stage: string; type: string; team: string; from: string; to: string; showCancelled: boolean }
const DEFAULT_LIST: ListPrefs = { sort: 'startsOn', asc: false, stage: '', type: '', team: '', from: '', to: '', showCancelled: false };
function readListPrefs(): ListPrefs {
  try { return { ...DEFAULT_LIST, ...JSON.parse(localStorage.getItem(LIST_PREFS) ?? '{}') as Partial<ListPrefs> }; } catch { return DEFAULT_LIST; }
}
const orgsOf = (e: EventRow) => [e.departmentName, ...(e.otherOrganizations ?? [])].join(', ');

export function EventList() {
  const { me } = useAuth();
  const [p, setP] = useState<ListPrefs>(readListPrefs);
  const set = (x: Partial<ListPrefs>) => setP((old) => { const n = { ...old, ...x }; try { localStorage.setItem(LIST_PREFS, JSON.stringify(n)); } catch { /* not remembered */ } return n; });
  const { data, error, reload } = useData<EventRow[]>(`/api/events${p.showCancelled ? '?cancelled=1' : ''}`);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const nav = useNavigate();
  const types = useMemo(() => [...new Set([...EVENT_TYPES, ...(data ?? []).map((e) => e.eventType).filter(Boolean) as string[]])], [data]);
  const teams = useMemo(() => [...new Set((data ?? []).map((e) => e.team).filter(Boolean) as string[])].sort(), [data]);
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const rows = (data ?? []).filter((e) => (!p.stage || e.stage === p.stage)
      && (!p.type || (p.type === '-' ? !e.eventType : e.eventType === p.type))
      && (!p.team || (p.team === '-' ? !e.team : e.team === p.team))
      && (!p.from || (e.endsOn ?? e.startsOn) >= p.from) && (!p.to || e.startsOn <= p.to)
      && words.every((w) => `${orgsOf(e)} ${e.location ?? ''} ${e.eventType ?? ''} ${e.team ?? ''}`.toLowerCase().includes(w)));
    const key = (e: EventRow): string | number => p.sort === 'org' ? orgsOf(e).toLowerCase() : p.sort === 'stage' ? STAGE_ORDER[e.stage] ?? 99
      : p.sort === 'assessments' ? e.assessments : ((e[p.sort] ?? '') as string).toLowerCase();
    return rows.sort((a, b) => { const x = key(a), y = key(b); const c = x < y ? -1 : x > y ? 1 : a.startsOn.localeCompare(b.startsOn); return p.asc ? c : -c; });
  }, [data, p, q]);
  const counts = useMemo(() => Object.fromEntries(STAGES.map((s) => [s.key, (data ?? []).filter((e) => e.stage === s.key).length])), [data]);
  const th = (k: SortKey, label: string, cls?: string) => (
    <th className={`sortable${cls ? ` ${cls}` : ''}`} aria-sort={p.sort === k ? (p.asc ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => set(p.sort === k ? { asc: !p.asc } : { sort: k, asc: k !== 'startsOn' && k !== 'assessments' })}>
        {label}{p.sort === k ? (p.asc ? ' ▲' : ' ▼') : ''}</button></th>);
  const filtered = !!(p.stage || p.type || p.team || p.from || p.to || q);
  return (
    <>
      <div className="spread"><h1>Testing events</h1>
        {me && isStaff(me.role) && <button onClick={() => setAdding(true)}>+ New event</button>}</div>
      <ErrorBox error={error} />
      <div className="card ev-filters">
        <input aria-label="Search events" placeholder="Search organization, location, type or team" value={q} onChange={(e) => setQ(e.target.value)} />
        <select aria-label="Status" value={p.stage} onChange={(e) => set({ stage: e.target.value })}>
          <option value="">All statuses</option>
          {STAGES.filter((s) => s.key !== 'cancelled' || p.showCancelled).map((s) => <option key={s.key} value={s.key}>{s.label} ({counts[s.key] ?? 0})</option>)}
        </select>
        <select aria-label="Type of event" value={p.type} onChange={(e) => set({ type: e.target.value })}>
          <option value="">All types</option>{types.map((t) => <option key={t}>{t}</option>)}<option value="-">No type</option></select>
        <select aria-label="Team" value={p.team} onChange={(e) => set({ team: e.target.value })}>
          <option value="">All teams</option>{teams.map((t) => <option key={t}>{t}</option>)}<option value="-">No team</option></select>
        <label className="small ev-date">From <input type="date" value={p.from} onChange={(e) => set({ from: e.target.value })} /></label>
        <label className="small ev-date">To <input type="date" value={p.to} onChange={(e) => set({ to: e.target.value })} /></label>
        <label className="small"><input type="checkbox" checked={p.showCancelled} onChange={(e) => set({ showCancelled: e.target.checked })} /> Show cancelled</label>
        {filtered && <button type="button" className="link small" onClick={() => { setQ(''); set({ stage: '', type: '', team: '', from: '', to: '' }); }}>Clear filters</button>}
      </div>
      <div className="card">
        {!data ? <p className="muted">Loading…</p> : !shown.length ? <p className="muted">{data.length ? 'No events match these filters.' : 'No events yet.'}</p> : (<>
          <p className="small muted" style={{ marginTop: 0 }}>{shown.length} event{shown.length === 1 ? '' : 's'}{filtered ? ` of ${data.length}` : ''}. Click a column heading to sort.</p>
          <div className="table-scroll"><table className="ev-table"><thead><tr>
            {th('startsOn', 'Date')}{th('org', 'Organization')}{th('eventType', 'Type')}{th('team', 'Team')}{th('location', 'Location')}{th('stage', 'Status')}{th('assessments', 'Visits', 'num')}
          </tr></thead>
            <tbody>{shown.map((e) => (
              <tr key={e.id} className="link" onClick={() => nav(`/events/${e.id}`)}>
                <td className="nowrap">{e.startsOn}{e.endsOn && e.endsOn !== e.startsOn ? <span className="muted"> – {e.endsOn.slice(5)}</span> : ''}</td>
                <td>{e.departmentName}{!!e.otherOrganizations?.length && <div className="muted small">with {e.otherOrganizations.join(', ')}</div>}</td>
                <td>{e.eventType ?? <span className="muted">—</span>}</td><td>{e.team ?? <span className="muted">—</span>}</td>
                <td>{e.location ?? <span className="muted">—</span>}</td>
                <td><span className={`badge stage-${e.stage}`}>{STAGE_LABEL[e.stage] ?? e.stage}</span></td>
                <td className="num">{e.assessments}{e.assessments > 0 && <span className="muted small"> ({e.signed} signed)</span>}</td>
              </tr>))}</tbody></table></div>
        </>)}
      </div>
      {adding && <NewEventDialog onClose={() => setAdding(false)} onCreated={(id) => { setAdding(false); void reload(); nav(`/events/${id}`); }} />}
    </>
  );
}

/** + New event: the host organization, any others, the date(s), location, type and team. */
function NewEventDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const depts = useData<Department[]>('/api/departments');
  const teams = useData<string[]>('/api/events/teams');
  const [f, setF] = useState({ departmentId: '', startsOn: localToday(), endsOn: '', location: '', eventType: '', team: '', others: [] as string[] });
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const body = { departmentId: f.departmentId, departmentIds: f.others.filter((o) => o !== f.departmentId), startsOn: f.startsOn,
        endsOn: f.endsOn && f.endsOn !== f.startsOn ? f.endsOn : null, location: f.location || null, eventType: f.eventType || null, team: f.team.trim() || null };
      let r = await post<{ id?: string; duplicateOf?: string; visits?: number }>('/api/events', body);
      if (r.duplicateOf) {
        // That organization already has an event on that day: open it, unless they really want a second one.
        const name = f.others.length ? 'One of these organizations' : depts.data?.find((d) => d.id === f.departmentId)?.name ?? 'This organization';
        const visits = r.visits ?? 0;
        if (window.confirm(`${name} already has an event on ${f.startsOn} (${visits} visit${visits === 1 ? '' : 's'}).\n\nOK = open that event.\nCancel = create a second event on the same day anyway.`)) {
          onCreated(r.duplicateOf);
          return;
        }
        r = await post<{ id: string }>('/api/events', { ...body, allowDuplicate: true });
      }
      onCreated(r.id!);
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  return (
    <Modal title="New event" onClose={onClose}>
      <form onSubmit={create} className="modal-form">
        <div className="field"><label htmlFor="ne-dept">Organization (host)</label>
          <select id="ne-dept" value={f.departmentId} onChange={(e) => setF({ ...f, departmentId: e.target.value })} required autoFocus>
            <option value="">Choose…</option>{depts.data?.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <ArchivedMatches onRestored={(id) => { void depts.reload(); setF((cur) => ({ ...cur, departmentId: id })); }} /></div>
        {f.departmentId && <div className="field"><label htmlFor="ne-other">Other organizations at this event</label>
          {f.others.length > 0 && <div className="chips" style={{ marginBottom: 4 }}>{f.others.map((o) => (
            <span key={o} className="chip">{depts.data?.find((d) => d.id === o)?.name}
              <button type="button" aria-label="Remove" onClick={() => setF({ ...f, others: f.others.filter((x) => x !== o) })}>×</button></span>))}</div>}
          <select id="ne-other" value="" onChange={(e) => e.target.value && setF({ ...f, others: [...f.others, e.target.value] })}>
            <option value="">{f.others.length ? '+ Another…' : 'None (add one if several test together)'}</option>
            {depts.data?.filter((d) => d.id !== f.departmentId && !f.others.includes(d.id)).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select></div>}
        <div className="row">
          <div className="field"><label htmlFor="ne-start">First day</label>
            <input id="ne-start" type="date" min="2000-01-01" max="2099-12-31" value={f.startsOn} onChange={(e) => setF({ ...f, startsOn: e.target.value })} required /></div>
          <div className="field"><label htmlFor="ne-end">Last day <span className="muted small">(if more than one)</span></label>
            <input id="ne-end" type="date" min={f.startsOn} max="2099-12-31" value={f.endsOn} onChange={(e) => setF({ ...f, endsOn: e.target.value })} /></div>
        </div>
        <div className="field"><label htmlFor="ne-loc">Location</label>
          <input id="ne-loc" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="e.g. Station 1, 123 Main St" /></div>
        <div className="row">
          <div className="field"><label htmlFor="ne-type">Type of event</label>
            <select id="ne-type" value={f.eventType} onChange={(e) => setF({ ...f, eventType: e.target.value })}>
              <option value="">—</option>{EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="field"><label htmlFor="ne-team">Team assigned</label>
            <input id="ne-team" list="ne-teams" value={f.team} maxLength={60} onChange={(e) => setF({ ...f, team: e.target.value })} placeholder="e.g. Team 2" />
            <datalist id="ne-teams">{[...new Set([...(teams.data ?? []), 'Team 1', 'Team 2', 'Team 3'])].map((t) => <option key={t} value={t} />)}</datalist></div>
        </div>
        <ErrorBox error={err} />
        <div className="actions"><button disabled={busy}>{busy ? 'Creating…' : 'Create event'}</button><button type="button" className="secondary" onClick={onClose}>Cancel</button></div>
      </form>
    </Modal>
  );
}

interface RosterRow { id: string; firstName: string; lastName: string; dob: string | null; sex: string | null; employeeId: string | null; assessmentId: string | null; status: string | null; lastAssessedOn: string | null; otherOrg: string | null; possibleDuplicate?: boolean; dateOff?: string | null;
  orgId: string | null; orgName: string | null; makeup: boolean; added: boolean }
interface EventOrg { id: string; name: string; host: boolean }
interface RemovedRow { id: string; firstName: string; lastName: string; employeeId: string | null; reason: string | null; removedAt: string; removedBy: string | null }
interface EventDoc extends EventRow { roster: RosterRow[]; removed: RemovedRow[]; organizations: EventOrg[];
  statusChangedOn: string | null; statusChangedBy: string | null;
  progress: { visits: number; unfinished: number; awaitingPhysician: number; signed: number; released: number; resultsOrdered: number } }

export function EventDetail() {
  const { id } = useParams();
  const { me } = useAuth();
  const { data, error, reload } = useData<EventDoc>(`/api/events/${id}`);
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<{ added: number; updated: number; problems: { row: number; problem: string }[] } | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [filter, setFilter] = useState('');
  const [starting, setStarting] = useState<string | null>(null);   // roster row whose Start shows the visit choices
  const [editing, setEditing] = useState(false);
  const [orgFilter, setOrgFilter] = useState('');   // '' = everyone, an organization id, or 'makeup'
  const [csvOrg, setCsvOrg] = useState('');
  const nav = useNavigate();
  if (error) return <ErrorBox error={error} />;
  if (!data) return <p className="muted">Loading…</p>;

  const importCsv = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null); setResult(null);
    try {
      setResult(await post(`/api/events/${data.id}/roster`, { csv, departmentId: csvOrg || undefined }));
      setCsv('');
      await reload();
    } catch (x) { setErr(x); }
  };
  const removeFromEvent = async (p: RosterRow) => {
    const reason = window.prompt(`Take ${p.firstName} ${p.lastName} off this event's roster? They stay in the organization; you can put them back.

Reason (optional):`, 'Not testing');
    if (reason === null) return;
    setErr(null);
    try { await post(`/api/events/${data.id}/roster/${p.id}/remove`, { reason: reason.trim() || undefined }); await reload(); } catch (x) { setErr(x); }
  };
  const putBack = async (p: RemovedRow) => {
    setErr(null);
    try { await post(`/api/events/${data.id}/roster/${p.id}/restore`); await reload(); } catch (x) { setErr(x); }
  };
  const start = async (p: RosterRow, visitType: VisitType) => {
    try {
      const r = await post<{ id: string }>(`/api/events/${data.id}/start/${p.id}`, { visitType });
      nav(`/assessments/${r.id}`);
    } catch (x) { setErr(x); }
  };
  const multi = data.organizations.length > 1 || data.roster.some((p) => p.makeup);
  const shown = data.roster.filter((p) => (!filter || `${p.lastName} ${p.firstName} ${p.employeeId ?? ''}`.toLowerCase().includes(filter.toLowerCase()))
    && (!orgFilter || (orgFilter === 'makeup' ? p.makeup : p.orgId === orgFilter && !p.makeup)));
  const makeups = data.roster.filter((p) => p.makeup);
  // Organizations with someone tested here (the event's, and make-ups' own): the reports can be made for each.
  const reportOrgs = [...data.organizations.map((o) => ({ id: o.id, name: o.name, makeup: false })),
    ...[...new Map(makeups.filter((p) => p.orgId && p.assessmentId).map((p) => [p.orgId!, p.orgName ?? ''])).entries()]
      .filter(([oid]) => !data.organizations.some((o) => o.id === oid)).map(([oid, name]) => ({ id: oid, name, makeup: true }))];
  const done = data.roster.filter((p) => p.assessmentId).length;

  return (
    <>
      <CoverSheetTopBar back={<Link to="/events">← Events</Link>} query={`departmentId=${data.departmentId}&eventId=${data.id}`} />
      <div className="card">
        <div className="spread" style={{ alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: 0 }}>{data.departmentName}</h1>
            {data.organizations.length > 1 && <p style={{ margin: '2px 0 0' }}>with {data.organizations.filter((o) => !o.host).map((o) => o.name).join(', ')}</p>}
          </div>
          <span className={`badge stage-${data.stage} stage-big`} title={STAGES.find((s) => s.key === data.stage)?.help}>{STAGE_LABEL[data.stage] ?? data.stage}</span>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>{data.startsOn}{data.endsOn && data.endsOn !== data.startsOn ? ` – ${data.endsOn}` : ''}
          {data.location ? ` · ${data.location}` : ''}{data.eventType ? ` · ${data.eventType}` : ''}{data.team ? ` · ${data.team}` : ''}
          {' · '}{done} of {data.roster.length} started · <button type="button" className="linkish" onClick={() => setEditing(true)}>Edit event</button></p>
        {data.startsOn < '2000-01-01' && <div className="alert error small">This event's date has the wrong year ({data.startsOn}). Use "Edit event" to fix it.</div>}
        <EventStatusBar data={data} onChange={reload} onEdit={() => setEditing(true)} />
      </div>
      {editing && <EditEventDialog data={data} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); await reload(); }} />}
      <EventOrgsCard data={data} onChange={reload} />
      <EventPacketCard eventId={data.id} departmentId={data.departmentId} startsOn={data.startsOn} orgs={reportOrgs}
        signedCount={data.roster.filter((p) => p.status === 'signed' || p.status === 'released').length}
        signedFor={(oid) => data.roster.filter((p) => p.orgId === oid && (p.status === 'signed' || p.status === 'released')).length} />
      <ScanPilesCard eventId={data.id} />
      <ErrorBox error={err} />
      {me && <TakeOffline eventId={data.id} me={me.displayName} />}
      <div className="card">
        <div className="spread">
          <h2 style={{ margin: 0 }}>Roster</h2>
          <div className="row" style={{ flex: '0 1 auto', gap: 6 }}>
            {multi && <select aria-label="Show organization" style={{ maxWidth: 220 }} value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
              <option value="">Everyone ({data.roster.length})</option>
              {data.organizations.map((o) => <option key={o.id} value={o.id}>{o.name} ({data.roster.filter((p) => p.orgId === o.id && !p.makeup).length})</option>)}
              {makeups.length > 0 && <option value="makeup">Make-ups from other organizations ({makeups.length})</option>}
            </select>}
            <input style={{ maxWidth: 260 }} aria-label="Filter roster" placeholder="Filter by name or ID" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
        </div>
        {!data.roster.length ? <p className="muted">No one on this organization's roster yet — import a CSV below.</p> : (
          <table style={{ marginTop: '0.75rem' }}><thead><tr><th>Name</th>{multi && <th>Organization</th>}<th>ID #</th><th>DOB</th><th>Last assessed</th><th>This visit</th></tr></thead>
            <tbody>{shown.map((p) => (<Fragment key={p.id}>
              <tr>
                <td><Link to={`/participants/${p.id}`}>{p.lastName}, {p.firstName}</Link>
                  {p.otherOrg && !p.makeup && <div className="muted small">main organization: {p.otherOrg}</div>}
                  {p.dateOff && <div className="small"><span className="badge void" title="The test date is far from this event: often a misread year on an imported form. Open the assessment and correct it (an import fix if it was signed on paper).">test date {p.dateOff}?</span></div>}
                  {p.possibleDuplicate && <div className="small"><span className="badge provisional" title="Another record has the same name and date of birth; an admin can merge them (Participants, Possible duplicates)">possible duplicate record</span></div>}</td>
                {multi && <td className="small">{p.orgName ?? '—'}{p.makeup && <> <span className="badge visit-type" title="Tested here with another organization's event; counts in this event and in their own organization">make-up</span></>}</td>}
                <td>{p.employeeId ?? '—'}</td><td>{p.dob ?? <span className="badge provisional">no DOB</span>}</td>
                <td>{p.lastAssessedOn ?? '—'}</td>
                <td>{p.assessmentId
                  ? <><span className={`badge ${p.status}`}>{p.status}</span> <button className="link" onClick={() => nav(`/assessments/${p.assessmentId}`)}>Open</button></>
                  : <><button className="secondary" onClick={() => setStarting(starting === p.id ? null : p.id)}>Start</button>{' '}
                    <button className="link" title="Not testing at this event" onClick={() => void removeFromEvent(p)}>Remove</button></>}</td>
              </tr>
              {starting === p.id && <tr><td colSpan={multi ? 6 : 5}><NewVisitChooser onPick={(t) => void start(p, t)} onCancel={() => setStarting(null)} /></td></tr>}
              </Fragment>))}</tbody></table>
        )}
        <AddMakeup eventId={data.id} onAdded={reload} onRoster={(pid) => data.roster.some((p) => p.id === pid)} />
        {data.removed.length > 0 && (
          <details style={{ marginTop: '0.75rem' }}>
            <summary className="small">Removed from this event ({data.removed.length})</summary>
            <table><tbody>{data.removed.map((p) => (
              <tr key={p.id}>
                <td><Link to={`/participants/${p.id}`}>{p.lastName}, {p.firstName}</Link></td><td>{p.employeeId ?? '—'}</td>
                <td className="small muted">{p.reason ?? ''}{p.removedBy ? ` · ${p.removedBy}` : ''}</td>
                <td><button className="link" onClick={() => void putBack(p)}>Put back</button></td>
              </tr>))}</tbody></table>
          </details>)}
      </div>
      <form className="card" onSubmit={importCsv}>
        <h2>Import roster (CSV)</h2>
        <p className="muted small">Header row with any of: First Name, Last Name, Middle, DOB (YYYY-MM-DD or M/D/YYYY), Sex, Employee ID, Email, Phone.
          People are matched on employee ID + DOB (or name + DOB); new people are added to {data.organizations.length > 1 ? 'the organization chosen below' : data.departmentName}.</p>
        {data.organizations.length > 1 && <div className="field"><label>This list is for<select value={csvOrg} onChange={(e) => setCsvOrg(e.target.value)}>
          {data.organizations.map((o) => <option key={o.id} value={o.host ? '' : o.id}>{o.name}{o.host ? ' (host)' : ''}</option>)}</select></label></div>}
        <div className="field">
          <input type="file" accept=".csv,text/csv" aria-label="Choose a CSV file" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setCsv(await f.text()); }} />
        </div>
        <div className="field"><textarea aria-label="Roster CSV" rows={6} className="mono" value={csv} onChange={(e) => setCsv(e.target.value)}
          placeholder={'Last Name,First Name,DOB,Sex,Employee ID\nSmith,Pat,4/12/1984,M,10234'} /></div>
        <button disabled={!csv.trim()}>Import</button>
        {result && (
          <div className={`alert ${result.problems.length ? 'info' : 'ok'}`} style={{ marginTop: '0.75rem' }}>
            {result.added} added, {result.updated} updated.
            {result.problems.length > 0 && <ul className="small">{result.problems.map((p) => <li key={p.row}>Row {p.row}: {p.problem}</li>)}</ul>}
          </div>
        )}
      </form>
      <DocumentsCard eventId={data.id} />
    </>
  );
}

/** Where the event stands, what is left to do, and the status buttons (Completed, Postpone, Cancel, Reopen). */
function EventStatusBar({ data, onChange, onEdit }: { data: EventDoc; onChange: () => Promise<void>; onEdit: () => void }) {
  const [ask, setAsk] = useState<null | 'completed' | 'postponed' | 'cancelled'>(null);
  const [note, setNote] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const pr = data.progress;
  const setStatus = async (status: string, statusNote?: string) => {
    setErr(null);
    try { await patch(`/api/events/${data.id}`, { status, statusNote: statusNote?.trim() || null }); setAsk(null); setNote(''); await onChange(); } catch (x) { setErr(x); }
  };
  const left = [pr.unfinished && `${pr.unfinished} not finished (draft)`, pr.awaitingPhysician && `${pr.awaitingPhysician} waiting for the physician`,
    pr.signed && `${pr.signed} signed, not released`, pr.resultsOrdered && `${pr.resultsOrdered} with results still ordered`].filter(Boolean) as string[];
  const manual = data.stage === 'completed' || data.stage === 'cancelled' || data.stage === 'postponed';
  const QUESTIONS = {
    completed: { title: 'Mark this event completed', label: 'Note (optional)', placeholder: 'e.g. Reports sent to Chief Smith 10/22', button: 'Mark completed' },
    postponed: { title: 'Postpone this event', label: 'Why (optional)', placeholder: 'e.g. Weather; new date to be set', button: 'Postpone' },
    cancelled: { title: 'Cancel this event', label: 'Why (optional)', placeholder: 'e.g. Department cancelled', button: 'Cancel event' },
  };
  return (
    <div className="ev-status">
      <div className="small">
        {manual
          ? <>{STAGE_LABEL[data.stage]}{data.statusChangedOn ? ` on ${data.statusChangedOn}` : ''}{data.statusChangedBy ? ` by ${data.statusChangedBy}` : ''}{data.statusNote ? `: ${data.statusNote}` : '.'}</>
          : <>{STAGES.find((s) => s.key === data.stage)?.help}
            {pr.visits > 0 && <> {pr.released} of {pr.visits} visit{pr.visits === 1 ? '' : 's'} released{left.length ? `; ${left.join(', ')}` : ''}.</>}</>}
      </div>
      <div className="actions" style={{ marginTop: 6 }}>
        {!manual && <button type="button" className={data.stage === 'finalizing' && !left.length ? '' : 'secondary'} onClick={() => setAsk('completed')}>Mark completed</button>}
        {!manual && <button type="button" className="secondary" onClick={() => setAsk('postponed')}>Postpone</button>}
        {!manual && <button type="button" className="secondary" disabled={pr.visits > 0} title={pr.visits ? 'This event has visits; void or move them first.' : undefined} onClick={() => setAsk('cancelled')}>Cancel event</button>}
        {data.stage === 'postponed' && <button type="button" onClick={onEdit}>Set the new date</button>}
        {manual && <button type="button" className="secondary" onClick={() => void setStatus('scheduled')}>Reopen{data.stage === 'postponed' ? ' without a date change' : ''}</button>}
      </div>
      <ErrorBox error={err} />
      {ask && (
        <Modal title={QUESTIONS[ask].title} onClose={() => setAsk(null)}>
          <form className="modal-form" onSubmit={(e) => { e.preventDefault(); void setStatus(ask, note); }}>
            {ask === 'completed' && left.length > 0 && <div className="alert info small">Still open at this event: {left.join(', ')}. You can mark it completed anyway.</div>}
            <div className="field"><label htmlFor="st-note">{QUESTIONS[ask].label}</label>
              <input id="st-note" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder={QUESTIONS[ask].placeholder} autoFocus /></div>
            <ErrorBox error={err} />
            <div className="actions"><button>{QUESTIONS[ask].button}</button><button type="button" className="secondary" onClick={() => setAsk(null)}>Back</button></div>
          </form>
        </Modal>)}
    </div>
  );
}

/** Edit event: dates, location, type and team (organizations are changed on the event page). */
function EditEventDialog({ data, onClose, onSaved }: { data: EventDoc; onClose: () => void; onSaved: () => Promise<void> }) {
  const teams = useData<string[]>('/api/events/teams');
  const [f, setF] = useState({ startsOn: data.startsOn, endsOn: data.endsOn ?? '', location: data.location ?? '', eventType: data.eventType ?? '', team: data.team ?? '' });
  const [err, setErr] = useState<unknown>(null);
  const save = async (e: FormEvent) => {
    e.preventDefault(); setErr(null);
    try {
      await patch(`/api/events/${data.id}`, { startsOn: f.startsOn, endsOn: f.endsOn && f.endsOn !== f.startsOn ? f.endsOn : null,
        location: f.location.trim() || null, eventType: f.eventType || null, team: f.team.trim() || null });
      await onSaved();
    } catch (x) { setErr(x); }
  };
  return (
    <Modal title="Edit event" onClose={onClose}>
      <form className="modal-form" onSubmit={save}>
        {data.stage === 'postponed' && <div className="alert info small">A new first day puts this postponed event back on the schedule.</div>}
        <div className="row">
          <div className="field"><label htmlFor="ee-start">First day</label>
            <input id="ee-start" type="date" min="2000-01-01" max="2099-12-31" value={f.startsOn} onChange={(e) => setF({ ...f, startsOn: e.target.value })} required autoFocus /></div>
          <div className="field"><label htmlFor="ee-end">Last day <span className="muted small">(if more than one)</span></label>
            <input id="ee-end" type="date" min={f.startsOn} max="2099-12-31" value={f.endsOn} onChange={(e) => setF({ ...f, endsOn: e.target.value })} /></div>
        </div>
        <div className="field"><label htmlFor="ee-loc">Location</label>
          <input id="ee-loc" value={f.location} maxLength={200} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
        <div className="row">
          <div className="field"><label htmlFor="ee-type">Type of event</label>
            <select id="ee-type" value={f.eventType} onChange={(e) => setF({ ...f, eventType: e.target.value })}>
              <option value="">—</option>{[...new Set([...EVENT_TYPES, ...(data.eventType ? [data.eventType] : [])])].map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="field"><label htmlFor="ee-team">Team assigned</label>
            <input id="ee-team" list="ee-teams" value={f.team} maxLength={60} onChange={(e) => setF({ ...f, team: e.target.value })} placeholder="e.g. Team 2" />
            <datalist id="ee-teams">{[...new Set([...(teams.data ?? []), 'Team 1', 'Team 2', 'Team 3'])].map((t) => <option key={t} value={t} />)}</datalist></div>
        </div>
        <ErrorBox error={err} />
        <div className="actions"><button>Save</button><button type="button" className="secondary" onClick={onClose}>Cancel</button></div>
      </form>
    </Modal>
  );
}

/** The organizations at this event: the host and any others (add, remove, make host). */
function EventOrgsCard({ data, onChange }: { data: EventDoc; onChange: () => Promise<void> }) {
  const depts = useData<Department[]>('/api/departments');
  const [adding, setAdding] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const act = async (fn: () => Promise<unknown>) => { setErr(null); try { await fn(); await onChange(); } catch (x) { setErr(x); } };
  const count = (oid: string) => data.roster.filter((p) => p.orgId === oid && !p.makeup);
  return (
    <div className="card">
      <div className="spread"><h2 style={{ margin: 0 }}>Organizations at this event</h2>
        <form className="row" style={{ flex: '0 1 440px', gap: 6, flexWrap: 'nowrap' }} onSubmit={(e) => { e.preventDefault(); if (adding) void act(async () => { await post(`/api/events/${data.id}/organizations`, { departmentId: adding }); setAdding(''); }); }}>
          <select aria-label="Add an organization" style={{ flex: 1, minWidth: 0 }} value={adding} onChange={(e) => setAdding(e.target.value)}>
            <option value="">Add an organization…</option>
            {depts.data?.filter((d) => !data.organizations.some((o) => o.id === d.id)).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <button className="secondary" style={{ flex: '0 0 auto' }} disabled={!adding}>Add</button>
        </form></div>
      <ErrorBox error={err} />
      <table className="small" style={{ marginTop: 6 }}><tbody>{data.organizations.map((o) => (
        <tr key={o.id}>
          <td><Link to={`/organizations/${o.id}`}>{o.name}</Link>{o.host && <> <span className="badge">host</span></>}</td>
          <td className="muted">{count(o.id).length} on the roster · {count(o.id).filter((p) => p.assessmentId).length} started</td>
          <td style={{ textAlign: 'right' }}>{!o.host && <>
            <button type="button" className="link" onClick={() => void act(() => post(`/api/events/${data.id}/host`, { departmentId: o.id }))}>Make host</button>{' · '}
            <button type="button" className="link" onClick={() => { if (window.confirm(`Take ${o.name} off this event? Its people leave this event's roster.`)) void act(() => api('DELETE', `/api/events/${data.id}/organizations/${o.id}`)); }}>Remove</button></>}</td>
        </tr>))}</tbody></table>
      {data.roster.some((p) => p.makeup) && <p className="small muted" style={{ marginBottom: 0 }}>
        Make-ups from other organizations: {data.roster.filter((p) => p.makeup).length}. They count in this event and in their own organization's results and reports.</p>}
    </div>
  );
}

interface PersonHit { id: string; firstName: string; lastName: string; dob: string | null; employeeId: string | null; departmentName: string | null }

/** Find someone from another organization and add them to this event as a make-up (walk-ins can simply be started on the day). */
function AddMakeup({ eventId, onAdded, onRoster }: { eventId: string; onAdded: () => Promise<void>; onRoster: (id: string) => boolean }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<PersonHit[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const search = async (e: FormEvent) => {
    e.preventDefault(); setErr(null);
    try { setHits(await get<PersonHit[]>(`/api/participants?limit=15&q=${encodeURIComponent(q.trim())}`)); } catch (x) { setErr(x); }
  };
  const add = async (p: PersonHit) => {
    setErr(null);
    try { await post(`/api/events/${eventId}/roster/${p.id}/add`); await onAdded(); setHits(null); setQ(''); setOpen(false); } catch (x) { setErr(x); }
  };
  if (!open) return <p className="small" style={{ marginBottom: 0 }}><button type="button" className="link" onClick={() => setOpen(true)}>+ Add a make-up from another organization</button></p>;
  return (
    <div className="card" style={{ marginTop: '0.75rem', background: 'var(--ngf-blue-soft)' }}>
      <form className="row" onSubmit={search} style={{ alignItems: 'end' }}>
        <div className="field"><label htmlFor="mk-q">Find the person (name, ID # or date of birth)</label>
          <input id="mk-q" value={q} onChange={(e) => setQ(e.target.value)} autoFocus /></div>
        <div className="field actions" style={{ flex: '0 0 auto' }}><button disabled={!q.trim()}>Search</button>
          <button type="button" className="secondary" onClick={() => { setOpen(false); setHits(null); }}>Close</button></div>
      </form>
      <ErrorBox error={err} />
      {hits && (hits.length === 0 ? <p className="small muted">No one on file matches. A new person can be added on the Participants page.</p> : (
        <table className="small"><tbody>{hits.map((p) => (
          <tr key={p.id}><td>{p.lastName}, {p.firstName}</td><td>{p.dob ?? ''}</td><td>{p.employeeId ?? ''}</td><td>{p.departmentName ?? 'No organization'}</td>
            <td>{onRoster(p.id) ? <span className="muted">already on this event</span> : <button type="button" className="secondary" onClick={() => void add(p)}>Add as make-up</button>}</td></tr>))}</tbody></table>))}
      <p className="small muted" style={{ marginBottom: 0 }}>A make-up is tested with their own organization: their results go to that organization, and they count in this event too.</p>
    </div>
  );
}

interface EventPacketRow {
  id: string; status: 'queued' | 'running' | 'done' | 'failed'; includeRestricted: boolean; error: string | null; pageCount: number | null;
  people: number | null; sizeBytes: number | null; createdAt: string; finishedAt: string | null; requestedBy: string | null;
  departmentId: string | null; departmentName: string | null;
}

/**
 * The reports for an event, chosen from three: the Screening summary (NGF's letter to the department, figures only), the
 * Executive summary (one page with names, to print) and the Full event packet (one PDF of everything, made by the worker).
 */
type EventReport = 'screening' | 'executive' | 'packet';
const EVENT_REPORTS: { key: EventReport; title: string; who: string; what: string }[] = [
  { key: 'screening', title: 'Screening summary', who: 'For the department · group figures, no names',
    what: 'NGF’s Health and Fitness Screening Summary letter: findings, averages and ranges, comparison with earlier years, areas of improvement and concern.' },
  { key: 'executive', title: 'Executive summary', who: 'For NGF and the department contact · one page, names',
    what: 'Counts (tested, cleared, referred), fitness and risk classes across the group, the follow-up list and the participant list.' },
  { key: 'packet', title: 'Full event packet (PDF)', who: 'Everything · confidential medical information',
    what: 'One PDF: the executive summary and participant list, then every signed participant’s report with their supporting documents.' },
];

function EventPacketCard({ eventId, departmentId, startsOn, signedCount: allSigned, orgs, signedFor }: { eventId: string; departmentId: string; startsOn: string; signedCount: number;
  orgs: { id: string; name: string; makeup: boolean }[]; signedFor: (orgId: string) => number }) {
  const { me } = useAuth();
  const nav = useNavigate();
  const { data, reload } = useData<EventPacketRow[]>(`/api/events/${eventId}/packets`);
  const [err, setErr] = useState<unknown>(null);
  const [pick, setPick] = useState<EventReport>('screening');
  // For one organization (each gets only its own people), or the whole event. One organization: always that one.
  const [forOrg, setForOrg] = useState('');
  const several = orgs.length > 1;
  const signedCount = forOrg ? signedFor(forOrg) : allSigned;
  const orgQ = forOrg ? `?org=${forOrg}` : '';
  const working = data?.some((p) => p.status === 'queued' || p.status === 'running');
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => void reload(), 4000);
    return () => clearInterval(t);
  }, [working, reload]);
  const make = async () => {
    setErr(null);
    try { await post(`/api/events/${eventId}/packets`, forOrg ? { departmentId: forOrg } : {}); await reload(); } catch (x) { setErr(x); }
  };
  const canOpenRestricted = !!me && canSeeRestricted(me.role);
  const chosen = EVENT_REPORTS.find((r) => r.key === pick)!;
  // No signed visits here: another event of the same organization on the same day may hold them (an event made twice).
  const all = useData<EventRow[]>(signedCount ? null : '/api/events');
  const twins = (all.data ?? []).filter((e) => e.id !== eventId && e.departmentId === departmentId && e.startsOn === startsOn && e.assessments > 0);
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Event reports</h2>
      <p className="muted small" style={{ marginTop: 0 }}>Choose the report to make. All three use this event's signed visits ({signedCount} so far{forOrg ? ` for ${orgs.find((o) => o.id === forOrg)?.name}` : ''}).</p>
      {several && <div className="field" style={{ maxWidth: 420 }}><label htmlFor="rep-org">For</label>
        <select id="rep-org" value={forOrg} onChange={(e) => setForOrg(e.target.value)}>
          <option value="">The whole event (everyone, all organizations)</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}{o.makeup ? ' (make-ups)' : ''} only ({signedFor(o.id)} signed)</option>)}
        </select>
        <span className="small muted">Each organization should get its own reports: they show only that organization's people.</span></div>}
      {!signedCount && <div className="alert info small">There are no signed visits at this event yet, so there is nothing to put in these reports.
        {twins.map((t) => <div key={t.id}><b>This organization has another event on {t.startsOn}</b> with {t.assessments} visit{t.assessments === 1 ? '' : 's'}: <Link to={`/events/${t.id}`}>open that event</Link>.</div>)}</div>}
      <div className="report-choices" role="radiogroup" aria-label="Report">
        {EVENT_REPORTS.map((r) => (
          <button key={r.key} type="button" role="radio" aria-checked={pick === r.key} className={`report-choice${pick === r.key ? ' on' : ''}`} onClick={() => setPick(r.key)}>
            <b>{r.title}</b><span className="small muted">{r.who}</span>
          </button>))}
      </div>
      <div className="report-chosen">
        <p style={{ margin: '0 0 0.5rem' }}>{chosen.what}{pick === 'packet' && <> It also includes labs, EKGs, authorizations and outside records{canOpenRestricted ? ', and drug screens' : ''}, then the event's documents. People not yet signed are listed as pending.</>}</p>
        <div className="actions">
          {pick === 'screening' && <button disabled={!signedCount} onClick={() => nav(`/events/${eventId}/screening-summary${orgQ}`)}>Open screening summary</button>}
          {pick === 'executive' && <button onClick={() => nav(`/events/${eventId}/summary${orgQ}`)}>Open executive summary</button>}
          {pick === 'packet' && <button onClick={() => void make()} disabled={!signedCount || working}>{working ? 'Making packet…' : 'Make full packet (PDF)'}</button>}
        </div>
      </div>
      <ErrorBox error={err} />
      {pick === 'packet' && !!data?.length && (
        <table style={{ marginTop: '0.5rem' }}>
          <thead><tr><th>Made</th><th>By</th><th>People</th><th>Pages</th><th>Status</th><th /></tr></thead>
          <tbody>{data.map((p) => (
            <tr key={p.id}>
              <td className="small">{new Date(p.finishedAt ?? p.createdAt).toLocaleString()}</td>
              <td className="small">{p.requestedBy ?? '—'}{p.includeRestricted && <span className="badge provisional" style={{ marginLeft: 6 }}>incl. drug screens</span>}
                {several && <div className="muted">{p.departmentName ? `${p.departmentName} only` : 'Whole event'}</div>}</td>
              <td>{p.people ?? '—'}</td><td>{p.pageCount ?? '—'}</td>
              <td>{p.status === 'done' ? <span className="badge released">ready</span>
                : p.status === 'failed' ? <span className="badge void" title={p.error ?? ''}>failed</span>
                : <span className="badge reviewed">{p.status === 'running' ? 'building…' : 'waiting…'}</span>}
                {p.status === 'failed' && p.error && <div className="muted small">{p.error}</div>}</td>
              <td>{p.status === 'done' && (!p.includeRestricted || canOpenRestricted) && <>
                <a href={`/api/events/${eventId}/packets/${p.id}/pdf`} target="_blank" rel="noopener">Open</a>{' · '}
                <a href={`/api/events/${eventId}/packets/${p.id}/pdf?download=1`}>Download</a></>}</td>
            </tr>))}</tbody>
        </table>)}
      {pick !== 'packet' && !!data?.some((p) => p.status === 'done') && <p className="small muted" style={{ marginBottom: 0 }}>
        <button className="link" onClick={() => setPick('packet')}>{data.filter((p) => p.status === 'done').length} full packet(s) already made</button></p>}
    </div>
  );
}
